/*
Copyright (c) 2015 Fabio Landoni (http://fabiolandoni.ch/)


Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and / or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/

export class Dictionary<T extends number | string | object, U> {
    // tslint:disable-next-line:variable-name
    private _keys: T[] = [];
    // tslint:disable-next-line:variable-name
    private _values: U[] = [];

    private undefinedKeyErrorMessage = 'Key is either undefined, null or an empty string.';

    private isEitherUndefinedNullOrStringEmpty(object: any): boolean {
        return (typeof object) === 'undefined' || object === null || object.toString() === '';
    }

    private checkKeyAndPerformAction(action: any, key: any, value?: any) {

        if (this.isEitherUndefinedNullOrStringEmpty(key)) {
            throw new Error(this.undefinedKeyErrorMessage);
        }

        return action(key, value);
    }


    public add(key: T, value: U): void {

        const addAction = (k: T, v: U): void => {
            if (this.containsKey(k)) {
                throw new Error('An element with the same key already exists in the dictionary.');
            }

            this._keys.push(k);
            this._values.push(v);
        };

        this.checkKeyAndPerformAction(addAction, key, value);
    }

    public remove(key: T): boolean {

        const removeAction = (k: T): boolean => {
            if (!this.containsKey(k)) {
                return false;
            }

            const index = this._keys.indexOf(k);
            this._keys.splice(index, 1);
            this._values.splice(index, 1);

            return true;
        };

        return (this.checkKeyAndPerformAction(removeAction, key)) as boolean;
    }

    public getValue(key: T): U|undefined {

        const getValueAction = (k: T): U|undefined => {
            if (!this.containsKey(k)) {
                return undefined;
            }

            const index = this._keys.indexOf(k);
            return this._values[index];
        };

        return this.checkKeyAndPerformAction(getValueAction, key);
    }

    public containsKey(key: T): boolean {

        const containsKeyAction = (k: T): boolean => {
            if (this._keys.indexOf(k) === -1) {
                return false;
            }
            return true;
        };

        return this.checkKeyAndPerformAction(containsKeyAction, key) as boolean;
    }

    public update(key: T, newValue: U): void {

        const changeValueForKeyAction = (k: T, v: U): void => {
            if (!this.containsKey(k)) {
                throw new Error('In the dictionary there is no element with the given key.');
            }

            const index = this._keys.indexOf(k);
            this._values[index] = v;
        };

        this.checkKeyAndPerformAction(changeValueForKeyAction, key, newValue);
    }

    get isEmpty() {
        return this._keys.length === 0;
    }

    get keys(): T[] {
        return this._keys;
    }

    get values(): U[] {
        return this._values;
    }

    get size(): number {
        return this._values.length;
    }
}
